import { useQuery } from '@tanstack/react-query';
import { Building2, DoorOpen, KeyRound, ShieldCheck, Users } from 'lucide-react';
import { Link } from 'react-router';
import type { CondominiumOverview } from '@brimostech/shared';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  PageHeader,
} from '@brimostech/ui';
import { ErrorState } from '@/components/error-state';
import { StatCard, StatCardSkeleton } from '@/components/stat-card';
import { api } from '@/lib/api-client';
import { firstName } from '@/lib/format';
import { useCan } from '@/lib/permissions';
import { useCurrentUser } from '../auth/auth-context';
import { canReadRegistry } from '../shell/navigation';
import { DashboardPage } from './dashboard-page';

/** Managers and the front desk get the dashboard; residents and unlinked accounts the overview. */
export function HomePage() {
  const user = useCurrentUser();
  const tenantId = user.activeCondominiumId;
  const canDashboard = useCan('events:read');
  if (tenantId !== null && canDashboard) return <DashboardPage tenantId={tenantId} />;
  return <OverviewPage />;
}

function OverviewPage() {
  const user = useCurrentUser();
  const tenantId = user.activeCondominiumId;
  const overview = useQuery({
    queryKey: ['condominium', 'current', tenantId],
    queryFn: () => api<CondominiumOverview>('/condominiums/current'),
    enabled: tenantId !== null,
  });
  const registry = canReadRegistry(user);

  return (
    <>
      <PageHeader
        title={`Olá, ${firstName(user.name)}`}
        description={
          tenantId ? 'Resumo do condomínio selecionado.' : 'Selecione um condomínio para começar.'
        }
      />

      {tenantId === null ? (
        user.isSuperadmin ? (
          <EmptyState
            icon={<ShieldCheck />}
            title="Modo operador"
            description="Escolha um condomínio no menu acima para ver os dados dele, ou abra o painel da plataforma."
            action={
              <Button asChild>
                <Link to="/operador">Abrir painel do operador</Link>
              </Button>
            }
          />
        ) : (
          <EmptyState
            icon={<Building2 />}
            title="Nenhum condomínio vinculado"
            description="Sua conta ainda não está ligada a um condomínio. Fale com a administração para receber o convite."
          />
        )
      ) : overview.isPending ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <StatCardSkeleton key={i} />
          ))}
        </div>
      ) : overview.isError ? (
        <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard label="Blocos" value={overview.data.counts.blocks} icon={Building2} />
            <StatCard
              label="Unidades"
              value={overview.data.counts.units}
              icon={DoorOpen}
              to={registry ? '/unidades' : undefined}
            />
            <StatCard
              label="Pessoas"
              value={overview.data.counts.people}
              icon={Users}
              to={registry ? '/pessoas' : undefined}
            />
            <StatCard
              label="Usuários com acesso"
              value={overview.data.counts.users}
              icon={KeyRound}
              hint="contas ativas neste condomínio"
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>{overview.data.name}</CardTitle>
              <CardDescription>Administrado por {overview.data.organization.name}</CardDescription>
            </CardHeader>
            <CardContent>
              <dl className="grid gap-4 text-sm sm:grid-cols-3">
                <div>
                  <dt className="text-muted-foreground">Cidade</dt>
                  <dd className="font-medium">
                    {overview.data.city
                      ? `${overview.data.city}${overview.data.state ? ` / ${overview.data.state}` : ''}`
                      : 'Não informada'}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Fuso horário</dt>
                  <dd className="font-medium">{overview.data.timezone}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Seu papel</dt>
                  <dd className="font-medium">{user.activeRole ?? '—'}</dd>
                </div>
              </dl>
            </CardContent>
          </Card>
        </>
      )}
    </>
  );
}
